import React from "react";
import Navbar from "./NavBar";
import Footer from "./Footer";
import { FaShieldAlt, FaUserLock, FaDatabase, FaShareAlt, FaKey, FaFileAlt } from "react-icons/fa";

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800 text-white">
      <Navbar />
      
      <div className="pt-24 pb-16">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-4xl font-bold mb-8 bg-gradient-to-r from-green-400 to-blue-500 text-transparent bg-clip-text">Privacy Policy</h1>
            <p className="text-gray-300 text-lg mb-8">
              At Escrownia, we're committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy outlines how we collect, use, and safeguard your data when you use our blockchain-based escrow services.
            </p>
            
            <div className="space-y-10">
              {/* Section 1 */}
              <div className="bg-gray-800 rounded-xl border border-gray-700 p-6 hover:border-green-500 transition-all duration-300">
                <div className="flex items-start mb-4">
                  <div className="bg-gradient-to-br from-green-500 to-blue-600 w-12 h-12 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <FaUserLock className="text-white text-xl" />
                  </div>
                  <h2 className="text-2xl font-semibold">Information We Collect</h2>
                </div>
                <div className="space-y-3 pl-16">
                  <p className="text-gray-300">
                    <span className="font-medium text-white">Blockchain Information:</span> We collect public blockchain addresses and transaction data necessary for processing escrow transactions.
                  </p>
                  <p className="text-gray-300">
                    <span className="font-medium text-white">Account Information:</span> When you create an account, we may collect your email address for communication and recovery purposes.
                  </p>
                  <p className="text-gray-300">
                    <span className="font-medium text-white">Usage Data:</span> We collect anonymous data about how you interact with our platform to improve functionality and user experience.
                  </p>
                </div>
              </div>
              
              {/* Section 2 */}
              <div className="bg-gray-800 rounded-xl border border-gray-700 p-6 hover:border-green-500 transition-all duration-300">
                <div className="flex items-start mb-4">
                  <div className="bg-gradient-to-br from-green-500 to-blue-600 w-12 h-12 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <FaDatabase className="text-white text-xl" />
                  </div>
                  <h2 className="text-2xl font-semibold">How We Use Your Information</h2>
                </div>
                <div className="space-y-3 pl-16">
                  <p className="text-gray-300">
                    <span className="font-medium text-white">Transaction Processing:</span> To facilitate and secure escrow transactions between parties.
                  </p>
                  <p className="text-gray-300">
                    <span className="font-medium text-white">Service Improvement:</span> To enhance our platform, improve user experience, and develop new features.
                  </p>
                  <p className="text-gray-300">
                    <span className="font-medium text-white">Communication:</span> To provide important updates about our services, security notices, and transaction status changes.
                  </p>
                  <p className="text-gray-300">
                    <span className="font-medium text-white">Legal Compliance:</span> To comply with applicable laws, regulations, and legal processes.
                  </p>
                </div>
              </div>
              
              {/* Section 3 */}
              <div className="bg-gray-800 rounded-xl border border-gray-700 p-6 hover:border-green-500 transition-all duration-300">
                <div className="flex items-start mb-4">
                  <div className="bg-gradient-to-br from-green-500 to-blue-600 w-12 h-12 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <FaShareAlt className="text-white text-xl" />
                  </div>
                  <h2 className="text-2xl font-semibold">Information Sharing</h2>
                </div>
                <div className="space-y-3 pl-16">
                  <p className="text-gray-300">
                    We do not sell or rent your personal information to third parties. We may share information in the following circumstances:
                  </p>
                  <ul className="list-disc list-inside text-gray-300 space-y-1">
                    <li>With service providers who help us operate our platform</li>
                    <li>When required by law, regulation, or legal process</li>
                    <li>In connection with a business transfer, merger, or acquisition</li>
                    <li>With your consent or at your direction</li>
                  </ul>
                </div>
              </div>
              
              {/* Section 4 */}
              <div className="bg-gray-800 rounded-xl border border-gray-700 p-6 hover:border-green-500 transition-all duration-300">
                <div className="flex items-start mb-4">
                  <div className="bg-gradient-to-br from-green-500 to-blue-600 w-12 h-12 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <FaShieldAlt className="text-white text-xl" />
                  </div>
                  <h2 className="text-2xl font-semibold">Data Security</h2>
                </div>
                <div className="space-y-3 pl-16">
                  <p className="text-gray-300">
                    We implement strong security measures to protect your data from unauthorized access, alteration, disclosure, or destruction. These include:
                  </p>
                  <ul className="list-disc list-inside text-gray-300 space-y-1">
                    <li>Encryption of sensitive data</li>
                    <li>Regular security audits</li>
                    <li>Blockchain-based verification systems</li>
                    <li>Secure smart contracts</li>
                  </ul>
                  <p className="text-gray-300 mt-3">
                    While we strive to use commercially acceptable means to protect your personal information, no method of transmission over the internet or electronic storage is 100% secure.
                  </p>
                </div>
              </div>
              
              {/* Section 5 */}
              <div className="bg-gray-800 rounded-xl border border-gray-700 p-6 hover:border-green-500 transition-all duration-300">
                <div className="flex items-start mb-4">
                  <div className="bg-gradient-to-br from-green-500 to-blue-600 w-12 h-12 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <FaKey className="text-white text-xl" />
                  </div>
                  <h2 className="text-2xl font-semibold">Your Rights</h2>
                </div>
                <div className="space-y-3 pl-16">
                  <p className="text-gray-300">
                    Depending on your location, you may have certain rights regarding your personal information:
                  </p>
                  <ul className="list-disc list-inside text-gray-300 space-y-1">
                    <li>Access and receive a copy of your personal information</li>
                    <li>Rectify inaccurate or incomplete information</li>
                    <li>Request deletion of your personal information</li>
                    <li>Restrict or object to processing of your information</li>
                    <li>Data portability</li>
                  </ul>
                  <p className="text-gray-300 mt-3">
                    To exercise these rights, please contact us at <a href="mailto:privacy@escrownia.com" className="text-blue-400 hover:text-blue-300 transition-colors">privacy@escrownia.com</a>.
                  </p>
                </div>
              </div>
              
              {/* Section 6 */}
              <div className="bg-gray-800 rounded-xl border border-gray-700 p-6 hover:border-green-500 transition-all duration-300">
                <div className="flex items-start mb-4">
                  <div className="bg-gradient-to-br from-green-500 to-blue-600 w-12 h-12 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <FaFileAlt className="text-white text-xl" />
                  </div>
                  <h2 className="text-2xl font-semibold">Policy Updates</h2>
                </div>
                <div className="space-y-3 pl-16">
                  <p className="text-gray-300">
                    We may update this Privacy Policy periodically to reflect changes in our practices or for legal, operational, or regulatory reasons. We will notify you of any material changes by:
                  </p>
                  <ul className="list-disc list-inside text-gray-300 space-y-1">
                    <li>Posting a notice on our website</li>
                    <li>Sending an email to the address associated with your account</li>
                    <li>Providing notification through our application</li>
                  </ul>
                  <p className="text-gray-300 mt-3">
                    The date of the latest revision will be indicated at the top of this policy.
                  </p>
                </div>
              </div>
            </div>
            
            {/* Contact Information */}
            <div className="mt-12 p-6 bg-gradient-to-r from-green-600 to-blue-700 rounded-xl text-center">
              <h2 className="text-2xl font-semibold mb-4">Contact Us</h2>
              <p className="text-lg mb-6">
                If you have any questions about this Privacy Policy or our data practices, please contact us:
              </p>
              <div className="inline-block mx-auto text-center">
                <p className="mb-2"><strong>Email:</strong> <a href="mailto:privacy@escrownia.com" className="text-white underline">privacy@escrownia.com</a></p>
                <p><strong>Address:</strong> 123 Blockchain Street, Tech City, 12345</p>
              </div>
            </div>
            
            <div className="mt-8 text-center text-gray-400 text-sm">
              Last updated: March 20, 2025
            </div>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default PrivacyPolicy;